import { useMemo } from 'react';
import type {
  DataTableColumn,
  SortConfig,
  FilterValue,
  DiscoveredValue,
} from '../types';

interface UseFilteredDataOptions<T> {
  data: T[];
  columns: DataTableColumn<T>[];
  sortConfig: SortConfig | null;
  filters: Record<string, FilterValue>;
  searchQuery: string;
  searchableColumns?: (keyof T & string)[];
}

interface UseFilteredDataReturn<T> {
  filteredData: T[];
  discoveredValues: Record<string, DiscoveredValue[]>;
}

/**
 * Apply filters and sorting to data
 */
export function useFilteredData<T extends Record<string, unknown>>({
  data,
  columns,
  sortConfig,
  filters,
  searchQuery,
  searchableColumns,
}: UseFilteredDataOptions<T>): UseFilteredDataReturn<T> {
  // Discover unique values for dynamic-enum columns
  const discoveredValues = useMemo(() => {
    const result: Record<string, DiscoveredValue[]> = {};

    columns.forEach((column) => {
      if (column.type === 'dynamic-enum' || column.type === 'multi-text') {
        const valueCounts = new Map<string, number>();
        const separator = column.discoverOptions?.separator ?? ', ';

        data.forEach((row) => {
          const cellValue = row[column.key];

          if (cellValue == null) return;

          if (column.type === 'multi-text' && typeof cellValue === 'string') {
            // Split comma-separated values
            cellValue.split(separator).forEach((v) => {
              const trimmed = v.trim();
              if (trimmed) {
                valueCounts.set(trimmed, (valueCounts.get(trimmed) ?? 0) + 1);
              }
            });
          } else if (Array.isArray(cellValue)) {
            // Handle array values
            cellValue.forEach((v) => {
              const strVal = String(v);
              valueCounts.set(strVal, (valueCounts.get(strVal) ?? 0) + 1);
            });
          } else {
            // Single value
            const strVal = String(cellValue);
            valueCounts.set(strVal, (valueCounts.get(strVal) ?? 0) + 1);
          }
        });

        // Convert to array and sort by count
        const discovered: DiscoveredValue[] = Array.from(valueCounts.entries())
          .map(([value, count]) => ({ value, count, label: value }))
          .filter((v) => v.count >= (column.discoverOptions?.minCount ?? 1))
          .sort((a, b) => b.count - a.count)
          .slice(0, column.discoverOptions?.maxOptions ?? 20);

        result[column.key] = discovered;
      }
    });

    return result;
  }, [data, columns]);

  // Apply filters
  const filteredData = useMemo(() => {
    let result = [...data];

    // Apply global search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      const columnsToSearch = searchableColumns
        ? columns.filter((c) => searchableColumns.includes(c.key))
        : columns.filter((c) => c.type === 'text' || c.type === 'dynamic-enum');

      result = result.filter((row) =>
        columnsToSearch.some((column) => {
          const value = row[column.key];
          if (value == null) return false;
          return String(value).toLowerCase().includes(query);
        })
      );
    }

    // Apply each filter
    Object.entries(filters).forEach(([columnKey, filter]) => {
      const column = columns.find((c) => c.key === columnKey);
      if (!column) return;

      result = result.filter((row) => {
        const value = row[columnKey];

        switch (filter.type) {
          case 'text': {
            if (!filter.value) return true;
            const strValue = String(value ?? '').toLowerCase();
            return strValue.includes(filter.value.toLowerCase());
          }

          case 'number': {
            const numValue = typeof value === 'number' ? value : parseFloat(String(value));
            if (isNaN(numValue)) return false;
            if (filter.min !== undefined && numValue < filter.min) return false;
            if (filter.max !== undefined && numValue > filter.max) return false;
            return true;
          }

          case 'date': {
            if (!value) return false;
            const dateValue = new Date(value as string);
            if (filter.from) {
              const from = new Date(filter.from);
              if (dateValue < from) return false;
            }
            if (filter.to) {
              const to = new Date(filter.to);
              to.setHours(23, 59, 59, 999);
              if (dateValue > to) return false;
            }
            return true;
          }

          case 'enum': {
            if (filter.selected.length === 0) return true;
            return filter.selected.includes(String(value));
          }

          case 'multi-enum': {
            const separator = column.discoverOptions?.separator ?? ', ';
            let cellValues: string[] = [];

            if (Array.isArray(value)) {
              cellValues = value.map(String);
            } else if (typeof value === 'string' && column.type === 'multi-text') {
              cellValues = value.split(separator).map((v) => v.trim()).filter(Boolean);
            } else if (value != null) {
              cellValues = [String(value)];
            }

            if (filter.selected.length === 0) return true;

            if (filter.mode === 'include') {
              // Row must contain at least one of the selected values
              return filter.selected.some((s) => cellValues.includes(s));
            } else {
              // Row must NOT contain any of the selected values
              return !filter.selected.some((s) => cellValues.includes(s));
            }
          }

          case 'boolean': {
            if (filter.value === null) return true;
            return Boolean(value) === filter.value;
          }

          default:
            return true;
        }
      });
    });

    // Apply sorting
    if (sortConfig) {
      const column = columns.find((c) => c.key === sortConfig.column);

      result.sort((a, b) => {
        const aVal = a[sortConfig.column];
        const bVal = b[sortConfig.column];

        // Handle null/undefined
        if (aVal == null && bVal == null) return 0;
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        let comparison = 0;

        // Type-aware comparison
        if (column?.type === 'number' || column?.type === 'currency') {
          comparison = Number(aVal) - Number(bVal);
        } else if (column?.type === 'date' || column?.type === 'datetime') {
          comparison = new Date(aVal as string).getTime() - new Date(bVal as string).getTime();
        } else if (column?.type === 'boolean') {
          comparison = (aVal ? 1 : 0) - (bVal ? 1 : 0);
        } else {
          comparison = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
        }

        return sortConfig.order === 'desc' ? -comparison : comparison;
      });
    }

    return result;
  }, [data, columns, filters, sortConfig, searchQuery, searchableColumns]);

  return { filteredData, discoveredValues };
}
